import React, { useEffect, useRef, useCallback } from "react";
import styled from "@emotion/styled";
import { MapBrowserEvent } from "ol";
import type { Feature } from "ol";
import type { Geometry } from "ol/geom";
import Overlay from "ol/Overlay";
import { getCenter } from "ol/extent";
import { toLonLat } from "ol/proj";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import { observer } from "mobx-react-lite";
import MapPixelDetails from "@screens/FieldsScreen/MapPixelDetails";
import MobileDrawer from "@components/MobileDrawer/MobileDrawer";
import useIsMobile from "@src/hooks/useIsMobile";
import MapToolbar from "@screens/FieldsScreen/MapToolbar";
import FieldViewSelect from "@screens/FieldsScreen/FieldViewSelect";
import Spinner from "@components/Spinner";
import MapYieldLegend from "@screens/FieldsScreen/MapYieldLegend";
import MapOverallYieldLegend from "@screens/FieldsScreen/MapOverallYieldLegend";
import MapYieldStatistics from "@screens/FieldsScreen/MapYieldStatistics";
import YieldAdjustPanel from "@screens/FieldsScreen/YieldAdjustPanel";
import YieldEvenPanel, { EvenPaneLabel } from "@screens/FieldsScreen/YieldEvenPanel";
import MapCameraStatistics from "@screens/FieldsScreen/MapCameraStatistics";
import MapNdviLegend from "@screens/FieldsScreen/MapNdviLegend";
import NdviDateStrip from "@screens/FieldsScreen/NdviDateStrip";
import ProductivityLegend from "@screens/FieldsScreen/ProductivityLegend";
import ProductivityEmptyState from "@screens/FieldsScreen/ProductivityEmptyState";
import FieldTracksPanel from "@screens/FieldsScreen/FieldTracksPanel";
import CompareMapPanel from "@screens/FieldsScreen/CompareMapPanel";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { TAB_BAR_HEIGHT } from "@components/MobileTabBar";
import { useConvert, useLang, useYield } from "@src/hooks/useLang";
import CustomLayerPanel from "@screens/FieldsScreen/CustomLayerPanel";
import AddCustomLayerDialog from "@screens/FieldsScreen/dialogs/AddCustomLayerDialog";
import { formatCellLabel } from "@screens/FieldsScreen/fieldLayers";

/*
 * Always render: Wrapper (flex row) → LeftPane (flex:1) → Root → OL map.
 * In compare mode, or while the yield evening previews side by side, a
 * RightPane appears next to LeftPane.
 * This avoids React remounting the Root div (which disconnects OpenLayers).
 */
const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: calc(var(--vh-full, 100vh) - ${TAB_BAR_HEIGHT}px - var(--tabbar-inset, env(safe-area-inset-bottom, 0px)));
  }
`;

const Pane = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  position: relative;
`;

const Root = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  & > .ol-viewport {
    height: 100% !important;
  }
`;

const targetId = "map-holder";

const LoadingLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;
  background: rgba(256, 256, 256, 0.5);
`;

const Divider = styled.div`
  width: 2px;
  background: rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
`;

const HexTooltip = styled.div`
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
`;

/* Marker dropped on a clicked cell so the pinned location is visible on the map. */
const PinMarker = styled.div`
  width: 16px;
  height: 16px;
  background: #68ad18;
  border: 2px solid #fff;
  border-radius: 50% 50% 50% 0;
  transform: rotate(-45deg);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  pointer-events: none;
`;

/* Desktop map-anchored details card. Hidden on mobile (a MobileDrawer is used). */
const PinCard = styled.div`
  width: 248px;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.2);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const Map = () => {
  const vm = useFieldsScreenVM();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<Overlay | null>(null);
  const pinElRef = useRef<HTMLDivElement>(null);
  const cardElRef = useRef<HTMLDivElement>(null);
  const pinOverlayRef = useRef<Overlay | null>(null);
  const cardOverlayRef = useRef<Overlay | null>(null);
  const { convertYield } = useConvert();
  const locYield = useYield();
  const lang = useLang();
  const isMobile = useIsMobile();

  // Units for the hovered / pinned cell label (fieldLayers.formatCellLabel).
  const cellLabelContext = useCallback(
    () => ({
      sizeUnit: vm.cameraStatistics[String(vm.selectedFieldId)]?.meta?.size_unit,
      customUnit: vm.activeCustomLayer?.unitLabel,
      convertYield,
      locYield,
      pcsLabel: lang.fields.cameraUnitPcs,
      stonesLabel: lang.fields.cameraCellStones,
    }),
    [vm, convertYield, locYield, lang]
  );

  const handleMapClick = useCallback(
    (e: MapBrowserEvent<any>) => {
      // While a shape is being authored — field boundary edit or trial
      // draw/reshape — every click belongs to the Draw/Modify interaction.
      // Pinning a cell here pops the "Open in Google Maps" details card over
      // the shape being drawn, which is pure noise mid-draw.
      if (vm.isFieldEdit || vm.isTrialDrawing) {
        if (vm.pinnedCell) vm.clearPinnedCell();
        return;
      }

      let handled = false;
      vm.map.forEachFeatureAtPixel(e.pixel, (feature, layer) => {
        if (handled) return;

        // While authoring a yield correction, clicks select instead of pin.
        // The correction layer sits above the hexes, so clicking an already
        // selected cell hits it first and deselects — a natural toggle.
        if (vm.isYieldAdjustEdit) {
          if (layer === vm.adjustLayer) {
            handled = vm.removeDraftFeature(feature as Feature<Geometry>);
            return;
          }
          if (layer === vm.heatmapLayer) {
            vm.toggleHexInDraft(feature as Feature<Geometry>);
            handled = true;
            return;
          }
          return;
        }

        // Click a yield/camera cell → pin it so its ground location can be
        // opened/shared in Google Maps. Mirrors the hover branch above.
        if (layer === vm.heatmapLayer) {
          const geom = feature.getGeometry();
          if (!geom) return;
          const center = getCenter(geom.getExtent()) as [number, number]; // EPSG:3857
          const [lng, lat] = toLonLat(center);

          const label = formatCellLabel(feature, vm.selectedFieldView, cellLabelContext());
          if (label == null) return;
          vm.setPinnedCell(
            { coord3857: center, lat, lng, label },
            feature as Feature<Geometry>
          );
          handled = true;
          return;
        }

        if (layer === vm.fieldsLayer) {
          vm.selectField(feature.getId() as string);
          handled = true;
        }
      });

      // Click on empty map (no cell, no field) dismisses any pinned cell.
      if (!handled) vm.clearPinnedCell();
    },
    [vm, cellLabelContext]
  );

  const handlePointerMove = useCallback(
    (e: MapBrowserEvent<any>) => {
      const overlay = overlayRef.current;
      if (!overlay) return;

      let found = false;
      // The cell/zone/swath under the pointer, mirrored onto the compare pane.
      let mirrored: Feature<Geometry> | null = null;
      vm.map.forEachFeatureAtPixel(e.pixel, (feature, layer) => {
        if (!mirrored && vm.isMirrorLayer(layer))
          mirrored = feature as Feature<Geometry>;
        if (found) return;
        if (layer === vm.heatmapLayer) {
          const label = formatCellLabel(feature, vm.selectedFieldView, cellLabelContext());
          if (label != null && tooltipRef.current) {
            tooltipRef.current.textContent = label;
            overlay.setPosition(e.coordinate);
            found = true;
          }
        }
      });

      if (!found) {
        overlay.setPosition(undefined);
      }

      vm.syncCursorToCompare(e.coordinate, mirrored);
    },
    [vm, cellLabelContext]
  );

  // Recalculate map size when the split opens or closes
  useEffect(() => {
    const timer = setTimeout(() => vm.map.updateSize(), 350);
    return () => clearTimeout(timer);
  }, [vm.isSplitView, vm.map]);

  useEffect(() => {
    vm.map.setTarget(targetId);

    const overlay = new Overlay({
      element: tooltipRef.current!,
      positioning: "bottom-center",
      offset: [0, -8],
      stopEvent: false,
    });
    overlayRef.current = overlay;
    vm.map.addOverlay(overlay);

    // Marker pin dropped on a clicked cell (non-interactive, passes clicks through).
    const pinOverlay = new Overlay({
      element: pinElRef.current!,
      positioning: "bottom-center",
      stopEvent: false,
    });
    pinOverlayRef.current = pinOverlay;
    vm.map.addOverlay(pinOverlay);

    // Interactive details card floating just above the pin (desktop). stopEvent:true
    // so its buttons receive clicks and don't fall through to the map.
    const cardOverlay = new Overlay({
      element: cardElRef.current!,
      positioning: "bottom-center",
      offset: [0, -14],
      stopEvent: true,
      autoPan: { animation: { duration: 200 } },
    });
    cardOverlayRef.current = cardOverlay;
    vm.map.addOverlay(cardOverlay);

    vm.map.on("click", handleMapClick);
    vm.map.on("pointermove", handlePointerMove);

    // OL has no "pointer left the map" event, and without one the ghost pointer
    // freezes on the compare pane wherever the cursor last was.
    const viewport = vm.map.getViewport();
    const handlePointerLeave = () => vm.hideCursorOnCompare();
    viewport.addEventListener("pointerleave", handlePointerLeave);

    return () => {
      vm.map.un("click", handleMapClick);
      vm.map.un("pointermove", handlePointerMove);
      viewport.removeEventListener("pointerleave", handlePointerLeave);
      vm.map.removeOverlay(overlay);
      vm.map.removeOverlay(pinOverlay);
      vm.map.removeOverlay(cardOverlay);
    };
  }, [vm, handleMapClick, handlePointerMove]);

  // Position the pin + details card at the pinned cell centre. The card is a
  // desktop-only map anchor; on mobile a MobileDrawer is used instead, so leave
  // the card overlay unpositioned (hidden) there.
  useEffect(() => {
    const pos = vm.pinnedCell?.coord3857;
    pinOverlayRef.current?.setPosition(pos);
    cardOverlayRef.current?.setPosition(isMobile ? undefined : pos);
  }, [vm.pinnedCell, isMobile]);

  return (
    <Wrapper>
      <Pane>
        <Root id={targetId}>
          <HexTooltip ref={tooltipRef} />
          <PinMarker ref={pinElRef} />
          <PinCard ref={cardElRef}>
            <MapPixelDetails />
          </PinCard>
          <MobileDrawer
            open={isMobile && !!vm.pinnedCell}
            onClose={vm.clearPinnedCell}
            height="auto"
          >
            <MapPixelDetails />
          </MobileDrawer>
          <MapToolbar />
          <FieldViewSelect />
          {vm.yieldEvenPane && (
            <EvenPaneLabel>{lang.fields.yieldEvenOriginalLabel}</EvenPaneLabel>
          )}
          <MapYieldLegend />
          <MapNdviLegend />
          <MapOverallYieldLegend />
          <MapYieldStatistics />
          <YieldAdjustPanel />
          <YieldEvenPanel />
          <MapCameraStatistics />
          <NdviDateStrip />
          <ProductivityLegend />
          <ProductivityEmptyState />
          <CustomLayerPanel />
          <FieldTracksPanel />
          {(vm.loading || vm.heatmapLoading || vm.tracksLoading || vm.yieldEvenLoading) && (
            <LoadingLayout>
              <Spinner />
            </LoadingLayout>
          )}
        </Root>
      </Pane>
      {vm.isSplitView && (
        <>
          <Divider />
          <Pane>
            <CompareMapPanel />
          </Pane>
        </>
      )}
      {/* Mounted once here, not inside the layer dropdown, so the wizard
          survives the dropdown closing and overlays both panes. */}
      {vm.selectedFieldId != null && (
        <AddCustomLayerDialog
          visible={vm.customLayerDialogVisible}
          fieldId={String(vm.selectedFieldId)}
          onClose={vm.closeCustomLayerDialog}
          onAdded={(layerId) =>
            vm.onCustomLayerAdded(String(vm.selectedFieldId), layerId)
          }
        />
      )}
    </Wrapper>
  );
};

export default observer(Map);
